import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mic, Upload, ChevronDown, ChevronUp, Trash2, TrendingUp, Quote, CheckCircle2, AlertCircle, Loader2, FileDown } from "lucide-react";

const scoreColor = (s) => (s >= 7.5 ? "#2B6E4F" : s >= 5 ? "#C48A31" : "#8B2E2E");

export function Sparkline({ points }) {
  if (!points || points.length < 2) return null;
  const w = 220, h = 48, pad = 6;
  const xs = points.map((_, i) => pad + (i * (w - 2 * pad)) / (points.length - 1));
  const ys = points.map((p) => h - pad - ((p / 10) * (h - 2 * pad)));
  const d = xs.map((x, i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${ys[i].toFixed(1)}`).join(" ");
  return (
    <svg width={w} height={h} data-testid="score-sparkline" className="overflow-visible">
      <path d={d} fill="none" stroke="var(--brand-primary)" strokeWidth="1.5" />
      {xs.map((x, i) => (
        <circle key={i} cx={x} cy={ys[i]} r="2.5" fill={scoreColor(points[i])} />
      ))}
    </svg>
  );
}

export function ScoreBar({ item }) {
  return (
    <div data-testid={`criterion-${item.key}`}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-medium">{item.name}</span>
        <span className="font-mono text-sm" style={{ color: scoreColor(item.score) }}>{item.score}/10</span>
      </div>
      <div className="h-1.5 bg-neutral-200 mt-1.5">
        <div className="h-full transition-all" style={{ width: `${(item.score / 10) * 100}%`, background: scoreColor(item.score) }} />
      </div>
      {item.comment && <p className="text-xs text-neutral-500 mt-1.5 leading-relaxed">{item.comment}</p>}
    </div>
  );
}

export function ReviewDetail({ review }) {
  const [showTranscript, setShowTranscript] = useState(false);
  const [full, setFull] = useState(null);
  useEffect(() => {
    api.get(`/call-reviews/${review.id}`).then((r) => setFull(r.data)).catch(() => {});
  }, [review.id]);
  const r = full || review;
  const downloadPdf = async () => {
    try {
      const res = await api.get(`/call-reviews/${review.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `call-review-${(r.completed_at || "").slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download report"); }
  };
  return (
    <div className="border-t hairline p-6 space-y-6 bg-[color:var(--brand-surface2)]" data-testid={`review-detail-${review.id}`}>
      <div className="flex items-center justify-between gap-4">
        {r.summary ? <p className="text-sm leading-relaxed italic text-neutral-700 flex-1">&ldquo;{r.summary}&rdquo;</p> : <span />}
        <button onClick={downloadPdf} className="shrink-0 inline-flex items-center gap-2 border px-4 py-2 text-xs font-semibold transition-colors hover:bg-[color:var(--brand-primary)] hover:text-white" style={{ borderColor: "var(--brand-primary)", color: "var(--brand-primary)" }} data-testid={`review-pdf-btn-${review.id}`}>
          <FileDown size={13} /> PDF report
        </button>
      </div>
      <div className="grid md:grid-cols-2 gap-x-10 gap-y-5">
        {(r.scores || []).map((s) => <ScoreBar key={s.key} item={s} />)}
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="border hairline bg-white p-5">
          <div className="label-eyebrow mb-3 flex items-center gap-2" style={{ color: "#2B6E4F" }}>
            <CheckCircle2 size={13} /> Strengths
          </div>
          <ul className="space-y-2 text-sm">
            {(r.strengths || []).map((s, i) => <li key={i} className="flex gap-2"><span style={{ color: "#2B6E4F" }}>+</span>{s}</li>)}
          </ul>
        </div>
        <div className="border hairline bg-white p-5">
          <div className="label-eyebrow mb-3 flex items-center gap-2" style={{ color: "#C48A31" }}>
            <TrendingUp size={13} /> Improvement areas
          </div>
          <ul className="space-y-2 text-sm">
            {(r.improvements || []).map((s, i) => <li key={i} className="flex gap-2"><span style={{ color: "#C48A31" }}>→</span>{s}</li>)}
          </ul>
        </div>
      </div>
      {(r.evidence || []).length > 0 && (
        <div>
          <div className="label-eyebrow mb-3">From your call</div>
          <div className="space-y-3">
            {r.evidence.map((e, i) => (
              <div key={i} className="border-l-2 pl-4" style={{ borderColor: "var(--brand-primary)" }}>
                <div className="flex gap-2 text-sm italic text-neutral-700"><Quote size={13} className="shrink-0 mt-0.5 opacity-40" />&ldquo;{e.quote}&rdquo;</div>
                <div className="text-xs text-neutral-500 mt-1">{e.note}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      {r.transcript && (
        <div>
          <button className="btn-ghost text-xs" onClick={() => setShowTranscript(!showTranscript)} data-testid="toggle-transcript">
            {showTranscript ? "Hide transcript" : "Show full transcript"}
          </button>
          {showTranscript && (
            <pre className="mt-3 text-xs whitespace-pre-wrap leading-relaxed max-h-80 overflow-y-auto border hairline bg-white p-4 font-sans">{r.transcript}</pre>
          )}
        </div>
      )}
    </div>
  );
}

function StatusChip({ status }) {
  if (status === "processing") return <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-2 py-0.5 border border-[#C48A31] text-[#C48A31]" data-testid="status-processing"><Loader2 size={10} className="animate-spin" /> Reviewing…</span>;
  if (status === "failed") return <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-2 py-0.5 bg-[#8B2E2E] text-white"><AlertCircle size={10} /> Failed</span>;
  return null;
}

export default function CallReview() {
  const [reviews, setReviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [openId, setOpenId] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(() => api.get("/call-reviews").then((r) => setReviews(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const anyProcessing = reviews.some((r) => r.status === "processing");
  useEffect(() => {
    if (!anyProcessing) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [anyProcessing, load]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/call-reviews", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Recording uploaded — your coach review is being prepared");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const del = async (r) => {
    if (!window.confirm("Delete this call review?")) return;
    await api.delete(`/call-reviews/${r.id}`);
    load();
  };

  const scored = reviews.filter((r) => r.status === "complete" && r.overall_score != null);
  const latest = scored.length ? scored[scored.length - 1].overall_score : null;

  return (
    <div className="space-y-10" data-testid="call-review-page">
      <div className="flex flex-wrap items-end justify-between gap-8">
        <div>
          <div className="label-eyebrow mb-3">Continuous improvement</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">Call Review</h1>
          <p className="text-neutral-500 mt-3 max-w-xl">Upload a real call recording. Your AI coach transcribes it and scores it against your team's rubric — growth is built day by day.</p>
        </div>
        {scored.length >= 2 && (
          <div className="border hairline bg-white p-5">
            <div className="label-eyebrow mb-2">Your trend · {scored.length} reviewed calls</div>
            <Sparkline points={scored.map((r) => r.overall_score)} />
            <div className="mt-1 text-sm">Latest <span className="font-mono font-semibold" style={{ color: scoreColor(latest) }}>{latest}/10</span></div>
          </div>
        )}
      </div>

      <div className="border-2 border-dashed hairline bg-white p-8 flex flex-wrap items-center justify-between gap-6" style={{ borderColor: "var(--brand-primary)" }}>
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 flex items-center justify-center" style={{ border: "1.5px solid var(--brand-primary)" }}>
            <Mic size={22} strokeWidth={1.25} className="text-[var(--brand-primary)]" />
          </div>
          <div>
            <div className="font-display text-xl">Upload a call recording</div>
            <div className="text-xs text-neutral-500 mt-0.5">mp3, m4a, wav, ogg… · up to 25MB (~25 min) · transcription + review takes 1-2 minutes</div>
          </div>
        </div>
        <input ref={fileRef} type="file" accept="audio/*,.mp3,.m4a,.wav,.ogg,.webm,.flac,.aac,.mp4" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="call-file-input" />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-2 bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-hover)] text-white px-7 py-3.5 font-semibold rounded-sm transition-colors disabled:opacity-60"
          data-testid="call-upload-btn"
        >
          {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
          {uploading ? "Uploading…" : "Choose recording"}
        </button>
      </div>

      <div className="space-y-3" data-testid="review-list">
        {reviews.length === 0 && (
          <div className="text-sm text-neutral-500 border hairline bg-white p-8 text-center" data-testid="no-reviews">
            No calls reviewed yet — upload your first recording above.
          </div>
        )}
        {[...reviews].reverse().map((r) => (
          <div key={r.id} className="border hairline bg-white" data-testid={`review-row-${r.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-4 p-5">
              <div className="min-w-0">
                <div className="font-medium text-sm truncate">{r.filename}</div>
                <div className="font-mono text-xs text-neutral-400 mt-0.5">{(r.created_at || "").slice(0, 16).replace("T", " ")}</div>
                {r.status === "failed" && <div className="text-xs text-[#8B2E2E] mt-1">{r.error}</div>}
              </div>
              <div className="flex items-center gap-4">
                <StatusChip status={r.status} />
                {r.status === "complete" && r.overall_score != null && (
                  <div className="font-display text-3xl" style={{ color: scoreColor(r.overall_score) }} data-testid={`overall-score-${r.id}`}>
                    {r.overall_score}<span className="text-sm text-neutral-400">/10</span>
                  </div>
                )}
                {r.status === "complete" && (
                  <button className="btn-ghost text-xs flex items-center gap-1" onClick={() => setOpenId(openId === r.id ? null : r.id)} data-testid={`review-toggle-${r.id}`}>
                    {openId === r.id ? <ChevronUp size={13} /> : <ChevronDown size={13} />} {openId === r.id ? "Close" : "View feedback"}
                  </button>
                )}
                <button className="text-neutral-300 hover:text-[#8B2E2E] transition-colors" onClick={() => del(r)} data-testid={`review-delete-${r.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            {openId === r.id && r.status === "complete" && <ReviewDetail review={r} />}
          </div>
        ))}
      </div>
    </div>
  );
}
